import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { User, Menu, X, ChevronDown, Globe, DollarSign, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logoutUser } from '../store/userSlice';
import LoginModal from './LoginModal';

export default function Header() {
 const [isMenuOpen, setIsMenuOpen] = useState(false);
 const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
 const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
 
 // 使用国际化
 const { t, i18n } = useTranslation();
 
 // 使用Redux状态替代本地用户状态
 const dispatch = useDispatch();
 const { user, isLoggedIn } = useSelector((state) => state.user);
 
 const location = useLocation();

 // 移除复杂的事件监听，使用页面刷新来确保状态同步

 // 根据登录状态动态生成导航项
 const getNavItems = () => {
 const items = [
 { name: t('navigation.home'), href: '/', requireAuth: false },
 { name: 'ChatGPT Plus', href: '/chatgpt-plus', requireAuth: false },
 { name: 'Product Detail', href: '/product-detail', requireAuth: false },
 // { name: t('navigation.aiFleet'), href: '/ai-fleet', requireAuth: true }, // Temporarily disabled
 { name: t('navigation.myOrders'), href: '/orders', requireAuth: true },
 { name: t('navigation.selfServiceRenewal'), href: '/renewal', requireAuth: false },
 { name: t('navigation.emailInbox'), href: '/email-inbox', requireAuth: false },
 { name: t('navigation.support'), href: '/support', requireAuth: false }
 ];
 
 // 如果用户已登录，返回所有菜单项；否则只返回不需要认证的菜单项
 return items.filter(item => !item.requireAuth || isLoggedIn);
 };

 const navItems = getNavItems();

 // 处理登录成功 - 现在由Redux管理，不需要本地处理
 const handleLoginSuccess = () => {
 setIsLoginModalOpen(false);
 };

 // 处理登出 - 使用Redux action
 const handleLogout = () => {
 dispatch(logoutUser());
 setIsUserMenuOpen(false);
 };

 // 检查当前路径是否匹配
 const isActiveRoute = (href) => {
 if (href === '/') {
 return location.pathname === '/';
 }
 return location.pathname.startsWith(href);
 };

 // 关闭移动端菜单
 const closeMobileMenu = () => {
 setIsMenuOpen(false);
 };

 return (
 <header className="site-header bg-cream-50/85 backdrop-blur-md sticky top-0 z-50 border-b border-ink-200">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="flex items-center justify-between h-16">

 {/* 左侧 - Logo */}
 <div className="flex items-center">
 <div className="flex-shrink-0">
 <Link
 to="/"
 className="flex items-center hover:opacity-80 transition-opacity duration-200"
 >
 <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 overflow-hidden border border-ink-200">
 <img
 src="/images/logo192.png"
 alt="紫刀AI Logo"
 className="w-full h-full object-cover"
 />
 </div>
 <div>
 <h1 className="text-base font-serif text-ink-900 leading-tight">{t('header.brandName')}</h1>
 <p className="text-[11px] text-ink-500 truncate max-w-[180px]">{t('header.brandSlogan')}</p>
 </div>
 </Link>
 </div>
 </div>

 {/* 中间 - 桌面端导航 */}
 <nav className="hidden xl:block flex-1">
 <div className="flex items-center justify-center space-x-1">
 {navItems.map((item) => (
 <Link
 key={item.href}
 aria-current={isActiveRoute(item.href) ? 'page' : undefined}
 to={item.href}
 className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
 isActiveRoute(item.href)
 ? 'text-ink-900 bg-cream-100'
 : 'text-ink-600 hover:text-ink-900 hover:bg-cream-100'
 }`}
 >
 {item.name}
 </Link>
 ))}
 </div>
 </nav>

 {/* 右侧 - 语言货币 + 用户登录 + 移动端菜单 */}
 <div className="flex items-center space-x-3">

 <div className="hidden xl:block">
 <div className="flex items-center space-x-1 text-sm text-ink-500">
 <DollarSign className="w-4 h-4" />
 <span>{t('header.currency')}</span>
 </div>
 </div>

 {/* 用户登录状态 */}
 {isLoggedIn && user ? (
 <div className="relative">
 <button
 onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
 className="hidden xl:flex items-center space-x-2 px-3 py-1.5 rounded-md text-ink-700 hover:text-ink-900 hover:bg-cream-100 transition-colors duration-200"
 >
 <div className="w-7 h-7 rounded-full flex items-center justify-center bg-accent-100 border border-accent-200">
 <span className="text-accent-700 text-sm font-semibold">
 {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
 </span>
 </div>
 <span className="text-sm font-medium">{user.name || user.email || t('common.user')}</span>
 <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
 </button>

 {isUserMenuOpen && (
 <div className="absolute right-0 mt-2 w-48 bg-cream-50 rounded-lg shadow-sm border border-ink-200 py-1.5 z-50">
 <Link
 to="/orders"
 className="flex items-center px-4 py-2 text-sm text-ink-700 hover:bg-cream-100 hover:text-ink-900 transition-colors duration-200"
 onClick={() => setIsUserMenuOpen(false)}
 >
 <span className="w-4 h-4 mr-3">📦</span>
 {t('header.userMenu.myOrders')}
 </Link>
 <div className="border-t border-ink-200 my-1" />
 <button
 onClick={handleLogout}
 className="flex items-center w-full px-4 py-2 text-sm text-ink-700 hover:bg-cream-100 hover:text-ink-900 transition-colors duration-200"
 >
 <LogOut className="w-4 h-4 mr-3" />
 {t('header.userMenu.logout')}
 </button>
 </div>
 )}
 </div>
 ) : (
 <button
 onClick={() => setIsLoginModalOpen(true)}
 className="hidden xl:inline-flex items-center space-x-2 px-4 py-2 rounded-md bg-ink-900 text-cream-50 hover:bg-ink-800 transition-colors duration-200"
 >
 <User className="h-4 w-4" />
 <span className="text-sm font-medium">{t('navigation.login')}</span>
 </button>
 )}

 {/* 移动端菜单按钮 */}
 <button
 aria-label={t(isMenuOpen ? 'fleetDesign.closeNavigation' : 'fleetDesign.openNavigation')}
 aria-expanded={isMenuOpen}
 onClick={() => setIsMenuOpen(!isMenuOpen)}
 className="xl:hidden p-2 rounded-md text-ink-700 hover:text-ink-900 hover:bg-cream-100 transition-colors duration-200"
 >
 {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
 </button>
 </div>
 </div>
 </div>

 {/* 移动端菜单 */}
 {isMenuOpen && (
 <div className="xl:hidden bg-cream-50 border-t border-ink-200">
 <div className="px-2 pt-2 pb-3 space-y-1">

 <div className="px-3 py-3 border-b border-ink-200 mb-3">
 <div className="flex items-center justify-center">
 <div className="flex items-center space-x-2 text-ink-600">
 <DollarSign className="w-4 h-4" />
 <span className="text-sm">{t('header.currency')}</span>
 </div>
 </div>
 </div>

 {navItems.map((item) => (
 <Link
 key={item.href}
 aria-current={isActiveRoute(item.href) ? 'page' : undefined}
 to={item.href}
 onClick={closeMobileMenu}
 className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
 isActiveRoute(item.href)
 ? 'text-ink-900 bg-cream-100'
 : 'text-ink-600 hover:text-ink-900 hover:bg-cream-100'
 }`}
 >
 {item.name}
 </Link>
 ))}

 <div className="border-t border-ink-200 pt-3 mt-3">
 {isLoggedIn && user ? (
 <>
 <div className="flex items-center px-3 py-2 text-ink-900">
 <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-accent-100 border border-accent-200">
 <span className="text-accent-700 text-sm font-semibold">
 {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
 </span>
 </div>
 <span className="font-medium">{user.name || user.email || t('common.user')}</span>
 </div>
 <button
 onClick={handleLogout}
 className="flex items-center px-3 py-2 text-ink-700 hover:text-ink-900 hover:bg-cream-100 rounded-md transition-colors duration-200 w-full"
 >
 <LogOut className="h-5 w-5 mr-3" />
 {t('navigation.logout')}
 </button>
 </>
 ) : (
 <button
 onClick={() => {
 setIsLoginModalOpen(true);
 closeMobileMenu();
 }}
 className="flex items-center px-3 py-2 text-ink-700 hover:text-ink-900 hover:bg-cream-100 rounded-md transition-colors duration-200 w-full"
 >
 <User className="h-5 w-5 mr-3" />
 {t('navigation.loginRegister')}
 </button>
 )}
 </div>
 </div>
 </div>
 )}

 {/* 点击外部关闭下拉菜单 */}
 {isUserMenuOpen && (
 <div 
 className="fixed inset-0 z-40" 
 onClick={() => {
 setIsUserMenuOpen(false);
 }}
 />
 )}

 {/* 登录弹窗 */}
 <LoginModal 
 isOpen={isLoginModalOpen}
 onClose={() => setIsLoginModalOpen(false)}
 onLogin={handleLoginSuccess}
 />
 </header>
 );
}